import { api } from "./client";
import type { PreApprovalRequestParty } from "@/types/deals";

/**
 * Deal Pipeline spec §3.6 — admin client for the master Pre-Approval document
 * request catalog (the `DocumentTypeRef` table), surfaced on the
 * "Document Request Templates" admin screen at
 * <c>/api/admin/document-request-templates</c>.
 *
 * "Delete" is a soft deactivate. Edits/additions/deactivations affect new
 * deals only — in-flight deals keep the document-request rows already spawned
 * for them.
 */
export interface DocumentRequestTemplateDto {
  code: string;
  displayName: string;
  description?: string | null;
  defaultParty: PreApprovalRequestParty;
  defaultRequired: boolean;
  defaultDueDays: number;
  defaultEscalationPath?: string | null;
  defaultNotes?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  modifiedAt: string;
}

export interface CreateDocumentRequestTemplatePayload {
  code: string;
  displayName: string;
  description?: string | null;
  defaultParty: PreApprovalRequestParty;
  defaultRequired: boolean;
  defaultDueDays?: number | null;
  defaultEscalationPath?: string | null;
  defaultNotes?: string | null;
  sortOrder?: number | null;
}

export interface UpdateDocumentRequestTemplatePayload {
  displayName: string;
  description?: string | null;
  defaultParty: PreApprovalRequestParty;
  defaultRequired: boolean;
  defaultDueDays: number;
  defaultEscalationPath?: string | null;
  defaultNotes?: string | null;
  sortOrder?: number | null;
}

const BASE = "/admin/document-request-templates";

export const documentRequestTemplatesApi = {
  list: (includeInactive = false) =>
    api.get<DocumentRequestTemplateDto[]>(BASE, { includeInactive }),
  get: (code: string) =>
    api.get<DocumentRequestTemplateDto>(`${BASE}/${encodeURIComponent(code)}`),
  create: (body: CreateDocumentRequestTemplatePayload) =>
    api.post<DocumentRequestTemplateDto>(BASE, body),
  update: (code: string, body: UpdateDocumentRequestTemplatePayload) =>
    api.put<DocumentRequestTemplateDto>(`${BASE}/${encodeURIComponent(code)}`, body),
  deactivate: (code: string) =>
    api.post<DocumentRequestTemplateDto>(`${BASE}/${encodeURIComponent(code)}/deactivate`),
  reactivate: (code: string) =>
    api.post<DocumentRequestTemplateDto>(`${BASE}/${encodeURIComponent(code)}/reactivate`),
};
